import { useState, isValidElement } from 'react';
import { omit } from 'lodash';
import clsx from 'clsx';
import { Icon } from '@/components/icon';

import type { FC } from 'react';
import type { PetInsuranceBlockType } from '@/lib/types';
import { DOG_INSURANCE_PLANS, CAT_INSURANCE_PLANS, DOG_HEADERS, CAT_HEADERS } from './plans';
import { BaseBlock } from '../base-block';
import {
  alignCenter,
  cell,
  cellFontMobile,
  cellHeaderMobile,
  cellMobile,
  cellTextHighlightMobile,
  cellTitle,
  evenRowGrayColor,
  expandButton,
  expandContainer,
  expandContainerMobile,
  expandIcon,
  gapBorderGray,
  gapBorderWhite,
  headerFont,
  headerWidth,
  mobileHeaderFont,
  mobileTable,
  nameCell,
  oddRowGrayColor,
  table,
  tableBody,
  thead,
  titleBox,
} from './styles.css';

import type { InsurancePlan, PlanValue, DisplayType } from './plans';

const cellDisplay = (value: PlanValue, type?: DisplayType) => {
  if (Array.isArray(value)) {
    if (type === 'bullet') {
      return (
        <ul>
          {value.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    }
    return value.join(', ');
  }
  if (isValidElement(value)) {
    return value;
  }
  return String(value);
};

export const PetInsuranceBlock: FC<PetInsuranceBlockType> = ({ title, petType }) => {
  const [expanded, toggleExpanded] = useState(false);

  const HEADERS = petType === 'Dog' ? DOG_HEADERS : CAT_HEADERS;
  const INSURANCE_PLANS = petType === 'Dog' ? DOG_INSURANCE_PLANS : CAT_INSURANCE_PLANS;
  const samplePlan = INSURANCE_PLANS[HEADERS[0]];

  const rows = Object.keys(omit(samplePlan, ['name'])) as Array<keyof Omit<InsurancePlan, 'name'>>;

  const rowsSlice = expanded ? rows : rows.slice(0, 5);

  const ExpandButton = () => (
    <button type="button" className={expandButton} onClick={() => toggleExpanded(!expanded)}>
      See {expanded ? 'less' : 'more'}
      <Icon className={expandIcon} name={expanded ? 'chevron-up' : 'chevron-down'} />
    </button>
  );

  return (
    <BaseBlock width="Normal" theme="Light" anchor="pet-insurance">
      <div className={clsx('d-flex', titleBox)}>
        <h2>{title}</h2>
      </div>
      <table className={table}>
        <thead className={thead}>
          <tr>
            <th className={headerWidth}> </th>
            {HEADERS.map((header) => (
              <th key={header} className={headerWidth}>
                <h3 className={headerFont}>{header}</h3>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={tableBody}>
          {rowsSlice.map((row) => (
            <tr key={row} className={evenRowGrayColor}>
              <td className={clsx(cell, nameCell, cellTitle)}>
                {cellDisplay(samplePlan[row].name, samplePlan[row].type)}
              </td>
              {HEADERS.map((header) => (
                <td key={header} className={cell}>
                  {cellDisplay(INSURANCE_PLANS[header][row].value, INSURANCE_PLANS[header][row].type)}
                </td>
              ))}
            </tr>
          ))}
          <tr className={evenRowGrayColor}>
            <td colSpan={HEADERS.length + 1}>
              <div className={clsx('d-flex', expandContainer)}>
                <ExpandButton />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <table className={mobileTable}>
        <thead className={thead}>
          <tr>
            <th className={clsx(alignCenter, headerWidth)}>
              <h3 className={mobileHeaderFont}>Coverage options</h3>
            </th>
          </tr>
        </thead>
        {rowsSlice.map((row, index) => {
          const isOdd = index % 2 === 0;

          return (
            <tbody key={row} className={clsx(oddRowGrayColor, tableBody)}>
              <tr>
                <td className={cellHeaderMobile}>{samplePlan[row].name}</td>
              </tr>
              {HEADERS.map((header) => (
                <tr key={header} className={!isOdd ? gapBorderWhite : gapBorderGray}>
                  <td className={clsx(cellMobile, cellFontMobile)}>
                    <span className={cellTextHighlightMobile}>{header}: </span>
                    {cellDisplay(INSURANCE_PLANS[header][row].value, INSURANCE_PLANS[header][row].type)}
                  </td>
                </tr>
              ))}
            </tbody>
          );
        })}
        <tbody className={clsx(oddRowGrayColor, tableBody)}>
          <tr>
            <td>
              <div className={clsx('d-flex', expandContainerMobile)}>
                <ExpandButton />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </BaseBlock>
  );
};
